import type {TreeNode, TreeNodeWithChildren} from './types';
import {isNullOrUndefined} from 'shared/utils/custom-utils';
import type {Childrenable} from '../types';
import {anyMatches, byIdOf, byReference, findFirstIndex} from 'shared/utils/array-utils';

export function isNodeExpanded<ID>(expandedNodes: TreeNode<ID>[], node: TreeNode<ID>): boolean {
    return anyMatches(expandedNodes, byIdOf(node));
}

export function isNodeChecked<ID>(checkedNodesIds: ID[], node: TreeNode<ID>): boolean {
    return anyMatches(checkedNodesIds, byReference(node.id));
}

export function isAnySubtreeNodeChecked<ID>(checkedNodesIds: ID[], node: TreeNodeWithChildren<ID>): boolean {
    if (checkedNodesIds.includes(node.id)) {
        return true;
    }
    return node.children.some((childNode) => isAnySubtreeNodeChecked(checkedNodesIds, childNode));
}

export function flattenTree<ID>(subTree: TreeNodeWithChildren<ID>): TreeNode<ID>[] {
    return [subTree, ...subTree.children.flatMap(flattenTree)]
}

export function findParentNodeInSubtree<ID>(subtree: TreeNodeWithChildren<ID>, node: TreeNode<ID>): TreeNodeWithChildren<ID> | undefined {
    if (anyMatches(subtree.children, byIdOf(node))) {
        return subtree;
    }

    for (const childNode of subtree.children) {
        const foundParent = findParentNodeInSubtree(childNode, node);
        if (!isNullOrUndefined(foundParent)) {
            return foundParent;
        }
    }

    return undefined;
}

export function findNextVisibleNode<ID>(subtree: TreeNodeWithChildren<ID>, node: TreeNode<ID>, expandedNodes: TreeNode<ID>[]): TreeNode<ID> {
    const visibleNodes = getVisibleNodesInPreorder(subtree, expandedNodes);
    return visibleNodes[findFirstIndex(visibleNodes, byIdOf(node)) + 1];
}

export function findPreviousVisibleNode<ID>(subtree: TreeNodeWithChildren<ID>, node: TreeNode<ID>, expandedNodes: TreeNode<ID>[]): TreeNode<ID> {
    const visibleNodes = getVisibleNodesInPreorder(subtree, expandedNodes);
    return visibleNodes[findFirstIndex(visibleNodes, byIdOf(node)) - 1];
}

export function findLastVisibleNode<ID>(subtree: TreeNodeWithChildren<ID>, node: TreeNode<ID>, expandedNodes: TreeNode<ID>[]): TreeNode<ID> {
    const visibleNodes = getVisibleNodesInPreorder(subtree, expandedNodes);
    return visibleNodes[visibleNodes.length - 1];
}

export function findFirstVisibleNode<ID>(subtree: TreeNodeWithChildren<ID>, node: TreeNode<ID>, expandedNodes: TreeNode<ID>[]): TreeNode<ID> {
    const visibleNodes = getVisibleNodesInPreorder(subtree, expandedNodes);
    return visibleNodes[0];
}

// preorder tree traversal (in order they are displayed to a user)
function getVisibleNodesInPreorder<ID>(tree: TreeNodeWithChildren<ID>, expanded: TreeNode<ID>[]): TreeNode<ID>[] {
    if (!isNodeExpanded(expanded, tree)) {
        return [tree]; // ignore children of collapsed nodes
    } else {
        return [tree, ...tree.children.flatMap((child) => getVisibleNodesInPreorder(child, expanded))];
    }
}

export function treeNodesEquals<ID>(node1: TreeNode<ID>, node2: TreeNode<ID>): boolean {
    return node1.id === node2.id;
}

export function createTreeNode<VALUE, ID>(value: Childrenable<VALUE>, disabledItems: Childrenable<VALUE>[], isDisabled: boolean, idResolver: (_: Childrenable<VALUE>) => ID): TreeNodeWithChildren<ID> {
    const id = idResolver(value);
    const disabled = isDisabled || disabledItems.some((disableValue) => idResolver(disableValue) === idResolver(value));
    const children = !value.children ? [] : value.children.map((child) => createTreeNode(child, disabledItems, isDisabled, idResolver));

    return {id, disabled, children};
}